import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiClient from '../client';
import { ENDPOINTS } from '../endpoints';
import { CreateTrackingJobDto } from '../types';
import { queryKeys } from '../keys';

const client = new ApiClient(process.env.API_URL!, 'app');

export const useTrackingJob = (shipmentId: string) =>
  useQuery({
    queryKey: queryKeys.trackingJob(shipmentId),
    queryFn: () => client.get(ENDPOINTS.TRACKING_JOB_SHIPMENT(shipmentId)),
    enabled: !!shipmentId,
    select: (data) => (data?.length > 0 ? data[0] : null),
  });

// Mutations
export const useCreateTrackingJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTrackingJobDto) => client.post(ENDPOINTS.TRACKING_JOBS, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackingJob(variables.shipmentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.trackingJobs });
      queryClient.invalidateQueries({ queryKey: ['shipmentEvents'] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
};

export const useCreateBulkTrackingJobs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTrackingJobDto[]) => client.post(ENDPOINTS.TRACKING_JOBS_BULK, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackingJobs });
      queryClient.invalidateQueries({ queryKey: ['shipmentEvents'] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
};

export const useResumeTrackingJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => client.put(ENDPOINTS.TRACKING_JOB_RESUME(jobId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackingJobs });
    },
  });
};

export const useExecuteTrackingJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => client.put(ENDPOINTS.TRACKING_JOB_EXECUTE(jobId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackingJobs });
      queryClient.invalidateQueries({ queryKey: ['shipmentEvents'] });
    },
  });
};

export const useDeleteTrackingJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => client.delete(ENDPOINTS.TRACKING_JOB(jobId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackingJobs });
    },
  });
};
